/**
 * Client-side attribution helper.
 *
 * Captures UTM parameters + referrer on the visitor's first page-hit and
 * persists them in sessionStorage for the rest of the session. Subsequent
 * conversions read from this store, so a Lead submitted on /kontakt can still
 * be attributed to the campaign that brought the visitor in via the landing
 * page.
 *
 * Strategy: **first-touch within session** (matches GA4 session_source default
 * and avoids losing campaign data when a visitor navigates internally).
 */

const STORAGE_KEY = "axc-attribution";

export interface Attribution {
  /** utm_source — e.g. "google", "facebook", "newsletter" */
  source?: string;
  /** utm_medium — e.g. "cpc", "social", "email" */
  medium?: string;
  /** utm_campaign — campaign name */
  campaign?: string;
  /** utm_term — paid keyword */
  term?: string;
  /** utm_content — ad creative / variant */
  content?: string;
  /** utm_id — campaign id (Google Ads gclid-style) */
  campaignId?: string;
  /** Google click id — used for offline conversion uploads */
  gclid?: string;
  /** Facebook/Meta click id — used for CAPI matching */
  fbclid?: string;
  /** document.referrer at first hit */
  referrer?: string;
  /** Landing page (window.location.href at first capture) */
  landingUrl?: string;
  /** Captured at (ISO timestamp) — useful for debugging/freshness */
  capturedAt?: string;
}

const UTM_KEYS: Array<[string, keyof Attribution]> = [
  ["utm_source", "source"],
  ["utm_medium", "medium"],
  ["utm_campaign", "campaign"],
  ["utm_term", "term"],
  ["utm_content", "content"],
  ["utm_id", "campaignId"],
  ["gclid", "gclid"],
  ["fbclid", "fbclid"],
];

function readFromUrl(): Partial<Attribution> {
  if (typeof window === "undefined") return {};
  try {
    const params = new URL(window.location.href).searchParams;
    const result: Partial<Attribution> = {};
    for (const [param, key] of UTM_KEYS) {
      const v = params.get(param);
      if (v) result[key] = v.slice(0, 200); // cap length
    }
    return result;
  } catch {
    return {};
  }
}

function readStored(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Attribution;
  } catch {
    return null;
  }
}

function writeStored(value: Attribution): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* sessionStorage may be blocked */
  }
}

/**
 * Captures attribution on first call per session. Idempotent: if a record
 * already exists in sessionStorage we keep the first-touch values. UTM
 * parameters that arrive on later page hits during the same session are
 * ignored — this avoids overwriting "google/cpc" with an internal nav.
 *
 * Call this once per page navigation (cheap — bails out early when stored).
 */
export function captureAttribution(): Attribution {
  if (typeof window === "undefined") return {};

  const existing = readStored();
  const fromUrl = readFromUrl();
  const hasUrlUtm = Object.keys(fromUrl).length > 0;

  // Already captured in this session → keep first-touch
  if (existing && !hasUrlUtm) return existing;

  // First hit OR new UTMs arrive (e.g. campaign override) → record
  if (!existing) {
    const record: Attribution = {
      ...fromUrl,
      referrer: document.referrer ? document.referrer.slice(0, 500) : undefined,
      landingUrl: window.location.href.slice(0, 500),
      capturedAt: new Date().toISOString(),
    };
    writeStored(record);
    return record;
  }

  // Session already has attribution but URL has fresh UTMs → respect first-touch.
  // Returning existing keeps the original campaign attribution intact.
  return existing;
}

/** Returns the attribution captured for this session, or `null`. */
export function getAttribution(): Attribution | null {
  return readStored();
}

/**
 * Returns a normalized attribution object safe to send to server functions.
 * Returns `undefined` when nothing meaningful has been captured (no UTMs,
 * no referrer, no click ids) so we don't pollute payloads with empty data.
 */
export function getAttributionForEvent(): Attribution | undefined {
  const a = readStored();
  if (!a) return undefined;
  const meaningful =
    a.source ||
    a.medium ||
    a.campaign ||
    a.term ||
    a.content ||
    a.gclid ||
    a.fbclid ||
    a.referrer;
  return meaningful ? a : undefined;
}
